import React from 'react';
import dayjs from 'dayjs';
import { SypacBox, SypacText } from '@sypac/component-library-react';
import { T } from '@tolgee/react';
import BoxComponent from '../../BoxComponent/BoxComponent';
import { ContractEditedNotificationProps } from './ContractEdited.interface';

const FIELD_LABELS: Record<string, string> = {
	quantity: 'Quantity',
	deliveryFrom: 'Delivery from',
	deliveryTo: 'Delivery to',
};

const formatChangeValue = (key: string, value: any): string => {
	if (value === null || value === undefined) return '-';

	if (typeof value === 'number') {
		return String(value);
	}

	if (typeof value === 'boolean') {
		return value ? 'Yes' : 'No';
	}

	if (typeof value === 'string') {
		const lowerKey = key.toLowerCase();
		const maybeDate = dayjs(value);
		if (
			maybeDate.isValid() &&
			(lowerKey.includes('date') ||
				lowerKey.includes('from') ||
				lowerKey.includes('to'))
		) {
			return maybeDate.format('DD.MM.YYYY');
		}
		return value;
	}

	return JSON.stringify(value);
};

const ContractEditedNotification: React.FC<ContractEditedNotificationProps> = ({
	notification,
	title,
	description,
	changes,
}) => {
	const entries = changes ? Object.entries(changes) : [];

	return (
		<BoxComponent notification={notification}>
			<div className="flex flex-col p-3 gap-3">
				<SypacText variant="body-regular-medium">
					<p className="text-gray-80">{title}</p>
				</SypacText>

				{description ? (
					<SypacText variant="overline-regular-large">
						<p className="text-gray-80 leading-3">{description}</p>
					</SypacText>
				) : null}

				{entries.length ? (
					<div className="flex flex-col items-start gap-3">
						<SypacBox className="bg-gray-1">
							<div className="flex flex-col gap-2.5 p-3">
								<SypacText variant="overline-regular-large">
									<p className="text-gray-40">
										<T keyName="contractEditedNotification.oldValues">
											Previous values
										</T>
									</p>
								</SypacText>
								<div className="grid grid-cols-3 gap-2.5">
									{entries.map(([key, value]) => (
										<React.Fragment key={`${key}-old`}>
											<div>
												<SypacText variant="overline-regular-large">
													<p className="text-gray-40">
														<T
															keyName={`contractEditedNotification.fields.${key}`}
														>
															{FIELD_LABELS[key] || key}
														</T>
													</p>
												</SypacText>
											</div>
											<div className="col-span-2">
												<SypacText variant="overline-regular-large">
													<p className="text-gray-80">
														{formatChangeValue(key, value.old)}
													</p>
												</SypacText>
											</div>
										</React.Fragment>
									))}
								</div>
							</div>
						</SypacBox>

						<SypacBox className="bg-gray-1">
							<div className="flex flex-col gap-2.5 p-3">
								<SypacText variant="overline-regular-large">
									<p className="text-gray-40">
										<T keyName="contractEditedNotification.newValues">
											New values
										</T>
									</p>
								</SypacText>
								<div className="grid grid-cols-3 gap-2.5">
									{entries.map(([key, value]) => (
										<React.Fragment key={`${key}-new`}>
											<div>
												<SypacText variant="overline-regular-large">
													<p className="text-gray-40">
														<T
															keyName={`contractEditedNotification.fields.${key}`}
														>
															{FIELD_LABELS[key] || key}
														</T>
													</p>
												</SypacText>
											</div>
											<div className="col-span-2">
												<SypacText variant="overline-regular-large">
													<p className="text-gray-80">
														{formatChangeValue(key, value.new)}
													</p>
												</SypacText>
											</div>
										</React.Fragment>
									))}
								</div>
							</div>
						</SypacBox>
					</div>
				) : null}
			</div>
		</BoxComponent>
	);
};

export default ContractEditedNotification;
